import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Download, Search, Users, Mail, FileText, Settings2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface Contact {
  id: string;
  email: string;
  name: string | null;
  source: string;
  language: string | null;
  tags: string[];
  newsletter_subscribed: boolean | null;
  created_at: string;
  updated_at: string;
}

const ContactsPage = () => {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [langFilter, setLangFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ['contacts'],
    queryFn: async () => {
      const { data } = await supabase.from('contacts').select('*').order('created_at', { ascending: false });
      return (data || []) as Contact[];
    },
  });

  const toggleNewsletter = useMutation({
    mutationFn: async ({ id, subscribed }: { id: string; subscribed: boolean }) => {
      await supabase.from('contacts').update({ newsletter_subscribed: subscribed }).eq('id', id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });

  const filtered = contacts.filter(c => {
    if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
    if (langFilter !== 'all' && c.language !== langFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.email.toLowerCase().includes(q) && !(c.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const exportCSV = () => {
    const rows = selectedIds.size > 0 ? filtered.filter(c => selectedIds.has(c.id)) : filtered;
    const csv = ['Email,Name,Source,Language,Newsletter,Tags,Date', ...rows.map(c =>
      `"${c.email}","${c.name || ''}","${c.source}","${c.language || 'en'}","${c.newsletter_subscribed}","${(c.tags || []).join('; ')}","${c.created_at}"`
    )].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'contacts.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const sourceIcon: Record<string, React.ReactNode> = {
    newsletter: <Mail className="w-3 h-3" />,
    contact_form: <FileText className="w-3 h-3" />,
    configurator: <Settings2 className="w-3 h-3" />,
    manual: <Users className="w-3 h-3" />,
  };

  const stats = {
    total: contacts.length,
    subscribed: contacts.filter(c => c.newsletter_subscribed).length,
    sources: [...new Set(contacts.map(c => c.source))].length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>Contacts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {stats.total} contacts · {stats.subscribed} newsletter subscribers
          </p>
        </div>
        <Button variant="outline" onClick={exportCSV} className="gap-2">
          <Download className="w-4 h-4" /> Export {selectedIds.size > 0 ? `(${selectedIds.size})` : 'All'}
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by email or name…" value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="newsletter">Newsletter</SelectItem>
            <SelectItem value="contact_form">Contact Form</SelectItem>
            <SelectItem value="configurator">Configurator</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={langFilter} onValueChange={setLangFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="ro">Romanian</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Newsletter</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} className={selectedIds.has(c.id) ? 'bg-muted/50' : ''}>
                <TableCell>
                  <Checkbox checked={selectedIds.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} />
                </TableCell>
                <TableCell style={{ color: 'var(--ink)' }}>{c.email}</TableCell>
                <TableCell className="text-muted-foreground">{c.name || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="gap-1 text-xs">
                    {sourceIcon[c.source] || null}
                    {c.source.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell><Badge variant="outline">{c.language || 'en'}</Badge></TableCell>
                <TableCell>
                  <Switch
                    checked={c.newsletter_subscribed ?? false}
                    onCheckedChange={v => toggleNewsletter.mutate({ id: c.id, subscribed: v })}
                  />
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {format(parseISO(c.created_at), 'MMM dd, yyyy')}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">No contacts found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ContactsPage;
